'use client';

import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';

import { Button } from '@/components/ui/Button';
import CembraLogo from '@/components/ui/icons/payment/svg/cembra.svg';
import { CEMBRA_MAX_FINANCING_TERM_MONTHS } from '@/lib/checkout/payments/methods/cembra/consts';
import { formatPrice } from '@/lib/utils/formatPrice';
import { modalActions } from '@/states/modalState';

import styles from './CembraSentence.module.scss';

/**
 * Tight crop of the shared `cembra.svg`. Its own `0 0 480 300` viewBox frames the
 * wordmark with ~75% empty height (the artwork sits at x 37.5–442.5, y 112.6–187.4),
 * which inline in a text row reads as a blank band above and below the sentence.
 * Re-framed here rather than in the asset, which checkout renders in a 56x33 box.
 */
const LOGO_VIEWBOX = '36 111 408 78';

export interface CembraSentenceProps {
  amount: number;
  currency: string;
  countryCode: string;
  ctaType: string;
  cembraEnabled?: boolean;
  cembraFinancingPaymentMax?: number;
  isCatalogMode?: boolean;
  className?: string;
}

/**
 * Swiss Cembra Pay teaser on PDP — monthly amount at max term + link to calculator modal.
 * Visibility matches legacy Breitling: CH, ORDER CTA, amount within market-config max.
 */
export function CembraSentence({
  amount,
  currency,
  countryCode,
  ctaType,
  cembraEnabled = false,
  cembraFinancingPaymentMax,
  isCatalogMode = false,
  className,
}: Readonly<CembraSentenceProps>) {
  const t = useTranslations('product.cembra');
  const locale = useLocale();

  if (
    isCatalogMode ||
    !cembraEnabled ||
    countryCode !== 'CH' ||
    ctaType !== 'ORDER' ||
    !amount ||
    cembraFinancingPaymentMax == null ||
    amount > cembraFinancingPaymentMax
  ) {
    return null;
  }

  const monthlyAmount = formatPrice(amount / CEMBRA_MAX_FINANCING_TERM_MONTHS, currency, locale);

  const openCalculator = () => {
    modalActions.open('finance-calculator', {
      amount,
      currency,
      locale,
      paymentType: 'cembra',
      cembraFinancingPaymentMax,
    });
  };

  return (
    <div className={clsx(styles.wrapper, className)} data-testid="CembraSentence">
      {/* Sentence, logo and link share one wrapping row (Figma) — they only break
          onto a second line when the info column is too narrow to hold them. */}
      <div className={styles.row}>
        <p className={styles.prefix} data-testid="CembraInstallmentAmountText">
          {t('sentence', { amount: monthlyAmount })}
        </p>
        <CembraLogo className={styles.logo} viewBox={LOGO_VIEWBOX} aria-hidden />
        <Button type="button" variant="link" onClick={openCalculator} data-testid="cembraLearnMore">
          {t('learnMore')}
        </Button>
      </div>
    </div>
  );
}

CembraSentence.displayName = 'CembraSentence';
